import { useMemo } from "react";
import type { BrandResponse } from "@/api";
import {
  addBrandTeamMember,
  addBrandUserMember,
  getBrandMembersOptions,
  getBrandMembersQueryKey,
  removeBrandTeamMember,
  removeBrandUserMember,
  updateBrandTeamMember,
  updateBrandUserMember,
} from "@/api";
import { type MembershipApi, MembershipPanel } from "@/components/build/MembershipPanel";
import { useCurrentUser } from "@/features/account/useCurrentUser";

/** Brands' Access tab: the membership model, via the shared MembershipPanel. */
export function BrandAccessTab({ brand }: Readonly<{ brand: BrandResponse }>) {
  const me = useCurrentUser();

  const api = useMemo<MembershipApi>(
    () => ({
      membersOptions: () => getBrandMembersOptions({ path: { brand_uuid: brand.uuid } }),
      membersQueryKey: () => getBrandMembersQueryKey({ path: { brand_uuid: brand.uuid } }),
      addUser: (id, role) =>
        addBrandUserMember({ path: { brand_uuid: brand.uuid }, body: { userUuid: id, role }, throwOnError: true }),
      updateUserRole: (id, role) =>
        updateBrandUserMember({ path: { brand_uuid: brand.uuid, user_uuid: id }, body: { role }, throwOnError: true }),
      removeUser: (id) =>
        removeBrandUserMember({ path: { brand_uuid: brand.uuid, user_uuid: id }, throwOnError: true }),
      updateTeamRole: (id, role) =>
        updateBrandTeamMember({ path: { brand_uuid: brand.uuid, team_uuid: id }, body: { role }, throwOnError: true }),
      removeTeam: (id) =>
        removeBrandTeamMember({ path: { brand_uuid: brand.uuid, team_uuid: id }, throwOnError: true }),
      addTeam: (id, role) =>
        addBrandTeamMember({ path: { brand_uuid: brand.uuid }, body: { teamUuid: id, role }, throwOnError: true }),
    }),
    [brand.uuid],
  );

  return <MembershipPanel api={api} currentUserUuid={me.data?.user.uuid} />;
}
